/**
 * What Amoreg has learned about how the signed-in person works in one
 * organisation (AMO-119), for Settings → Your context.
 *
 * Plain request functions over the workspace server's
 * `/api/amoreg/learned-notes` routes, which check the session is a member of
 * the organisation and scope every read and write to that membership. Each
 * resolves to data or to a message the settings page can show.
 *
 * @module state/learnedNotes
 */
import * as Effect from "effect/Effect";
import * as Schema from "effect/Schema";

import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary/target";

export const LearnedNoteSchema = Schema.Struct({
  id: Schema.String,
  category: Schema.Literals(["style", "workflow", "preference"]),
  text: Schema.String,
  reason: Schema.String,
  lastConfirmedAt: Schema.String,
  expiresAt: Schema.String,
});
export type LearnedNote = typeof LearnedNoteSchema.Type;

const LearnedNotesSchema = Schema.Struct({
  mode: Schema.Literals(["off", "shadow", "on"]),
  /** False for someone who can see the organisation without belonging to it. */
  member: Schema.Boolean.pipe(Schema.withDecodingDefault(Effect.succeed(true))),
  notes: Schema.Array(LearnedNoteSchema),
});
export type LearnedNotes = typeof LearnedNotesSchema.Type;

const decodeList = Schema.decodeUnknownEffect(LearnedNotesSchema);
const decodeUpdated = Schema.decodeUnknownEffect(Schema.Struct({ note: LearnedNoteSchema }));

// The helper sets `pathname` and encodes it, so the query must go in its own
// argument: a `?` inside the path arrives as `%3F` and the route never matches.
const url = (path: string, organizationId: string) =>
  resolvePrimaryEnvironmentHttpUrl(`/api/amoreg/learned-notes${path}`, { organizationId });

const post = (path: string, organizationId: string, body?: unknown) =>
  fetch(url(path, organizationId), {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

const failure = async (response: Response, fallback: string) =>
  (await response.text().catch(() => "")) || fallback;

/** Null on failure, so the page shows an error rather than an empty list. */
export async function fetchLearnedNotes(organizationId: string): Promise<LearnedNotes | null> {
  try {
    const response = await fetch(url("", organizationId), { credentials: "include" });
    if (!response.ok) return null;
    return await Effect.runPromise(decodeList(await response.json()));
  } catch {
    return null;
  }
}

export async function updateLearnedNote(
  organizationId: string,
  id: string,
  text: string,
): Promise<{ ok: true; note: LearnedNote } | { ok: false; message: string }> {
  const fallback = "The note could not be saved.";
  try {
    const response = await post("/update", organizationId, { id, text });
    if (!response.ok) return { ok: false, message: await failure(response, fallback) };
    const { note } = await Effect.runPromise(decodeUpdated(await response.json()));
    return { ok: true, note };
  } catch {
    return { ok: false, message: fallback };
  }
}

/** Resolves to an error message, or null once the note is gone. */
export async function deleteLearnedNote(organizationId: string, id: string): Promise<string | null> {
  const fallback = "The note could not be deleted.";
  try {
    const response = await post("/delete", organizationId, { id });
    // The server answers 204 for a note already gone; any 404 is a refusal.
    return response.ok ? null : await failure(response, fallback);
  } catch {
    return fallback;
  }
}

/** Forget everything learned in this organisation; an error message, or null. */
export async function forgetLearnedNotes(organizationId: string): Promise<string | null> {
  const fallback = "Your notes could not be cleared.";
  try {
    const response = await post("/forget", organizationId);
    return response.ok ? null : await failure(response, fallback);
  } catch {
    return fallback;
  }
}
